// Omnix/client/ui/NotesPanel.js
import { ComponentBase } from "../core/ComponentBase.js";
import { createAvatarElement } from "../../helpers/avatarHelper.js";

export class NotesPanel extends ComponentBase {
    static styles = `
        .notes-panel {
            display: flex;
            flex-direction: column;
            height: 100%; /* Fill the available space in the sidebar tab */
        }
        .notes-panel-list {
            list-style: none;
            padding: 0;
            margin: 0;
            flex-grow: 1; /* Allow the list to take up available space */
            overflow-y: auto; /* Enable scrolling for the list */
        }
        .note-item {
            display: flex;
            gap: 0.75rem;
            padding: 0.75rem 0;
            border-top: 1px solid var(--bs-border-color-translucent);
        }
        .note-item:first-child {
            border-top: none;
        }
        .note-body { flex-grow: 1; }
        .note-author { font-weight: 600; }
        .note-timestamp { font-size: 0.75rem; color: var(--bs-secondary-color); margin-left: 0.5rem; }
        .note-content {
            font-size: 0.9rem;
            margin-top: 0.25rem;
            color: var(--bs-body-color);
        }
        .note-content p:last-child { margin-bottom: 0; }
        .add-note-form {
            display: flex;
            gap: 0.5rem;
            margin-top: 1rem;
            padding-top: 1rem;
            border-top: 1px solid var(--bs-border-color);
            flex-shrink: 0; /* Prevent the form from shrinking */
        }
        .add-note-form textarea {
            border-radius: 18px;
            resize: none;
            min-height: 38px;
            max-height: 100px;
            line-height: 1.5;
        }
        .add-note-form button {
            align-self: flex-end; /* Align button to the bottom */
            margin-bottom: 0.375rem; /* Align with textarea padding */
        }
    `;

    constructor(config) {
        super(config);
        this.conversationId = null;
        this.notes = [];
        this.applyStyles();
    }

    async setConversation(conversation) {
        const newId = conversation ? conversation._id : null;
        if (this.conversationId !== newId) {
            this.conversationId = newId;
            await this.loadNotes();
        }
    }

    async loadNotes() {
        if (!this.conversationId) {
            this.notes = [];
            if (this.container) this.render();
            return;
        }
        try {
            // --- START OF FIX ---
            // The RPC call was missing, this is the core logic to fetch notes.
            this.notes = await this.appClient.rpcClient.call('UnifiedInboxRpc.getInternalNotes', {
                conversationId: this.conversationId
            });
            // --- END OF FIX ---
            if (this.container) this.render();
        } catch (error) {
            this.log.error("Failed to load internal notes:", error);
            if (this.container) this.container.innerHTML = `<p class="text-danger p-2">${this.t('Could not load notes.')}</p>`;
        }
    }

    render() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'notes-panel';
        }
        this.container.innerHTML = ''; // Clear

        const list = document.createElement('ul');
        list.className = 'notes-panel-list';
        if (!this.conversationId) {
            // Don't show anything if no conversation is selected
        } else if (this.notes.length === 0) {
            list.innerHTML = `<li class="text-muted small p-2 text-center">${this.t('No internal notes yet.')}</li>`;
        } else {
            this.notes.forEach(note => {
                // The author object is now enriched by the service
                const author = note.author || { displayName: 'Unknown' };
                const avatar = createAvatarElement({ displayName: author.displayName, avatarUrl: author.avatarUrl, sizeClass: 'small' });
                const li = document.createElement('li');
                li.className = 'note-item';
                li.innerHTML = `
                    ${avatar.outerHTML}
                    <div class="note-body">
                        <div>
                            <span class="note-author">${author.displayName}</span>
                            <span class="note-timestamp">${new Date(note.createdAt).toLocaleString()}</span>
                        </div>
                        <div class="note-content">${marked.parse(note.content)}</div>
                    </div>
                `;
                list.appendChild(li);
            });
        }
        this.container.appendChild(list);

        // Only show the form if a conversation is active
        if (this.conversationId) {
            const form = document.createElement('form');
            form.className = 'add-note-form';
            form.innerHTML = `
                <textarea class="form-control form-control-sm" placeholder="${this.t('Add an internal note...')}" rows="1"></textarea>
                <button type="submit" class="btn btn-primary btn-sm">${this.t('Post')}</button>
            `;
            const textarea = form.querySelector('textarea');
            textarea.addEventListener('input', () => {
                textarea.style.height = 'auto';
                textarea.style.height = `${textarea.scrollHeight}px`;
            });
            form.addEventListener('submit', (e) => {
                e.preventDefault();
                const content = textarea.value.trim();
                if (content) {
                    this.addNote(content);
                    textarea.value = '';
                    textarea.style.height = 'auto';
                }
            });
            this.container.appendChild(form);
        }

        return this.container;
    }

    async addNote(content) {
        try {
            await this.appClient.rpcClient.call('UnifiedInboxRpc.addInternalNote', {
                conversationId: this.conversationId,
                content: content
            });
            // The real-time event will trigger the reload, no need to call loadNotes() here
        } catch (error) {
            this.log.error("Failed to add note:", error);
            this.appClient.notifier.error("Could not post note.");
        }
    }
    
    handleNewNote(note) {
        if (note.conversationId === this.conversationId) {
            // The note from the server is already enriched with author details
            this.notes.push(note);
            this.render(); // Re-render to show the new note
        }
    }
}